import {ErrorHandlingComponent} from '../error-handling.component';
import { AfterViewInit, Input, OnInit, ViewChild, Directive } from '@angular/core';
import {BankAccount} from '../model/bank-account';
import {BankAccountDetailsComponent} from '../bank-account-details/bank-account-details.component';
import {FormGroup} from '@angular/forms';
import {BankInteractor} from '../interactors/bank.interactor';
import {BankAccountInteractor} from '../interactors/bank-account.interactor';
import {UserInteractor} from '../interactors/user.interactor';
import {Logger} from '../utils/Logger';
import {LoanApplicationInteractor} from '../interactors/loan-application.interactor';
import {isEmpty} from 'lodash';
import * as lodash from 'lodash';

@Directive()
export abstract class BankDetailsComponent extends ErrorHandlingComponent implements OnInit, AfterViewInit {
    public showAccountDetailsSection: boolean;
    public searchBankFormGroup: FormGroup;

    @Input()
    public bankAccount: BankAccount;

    @ViewChild(BankAccountDetailsComponent)
    public bankAccountDetailsChildComponent: BankAccountDetailsComponent;

    protected errorMessages(): object {
        return {
            bankName: [{
                type: 'required',
                message: 'Please select a bank'
            }],
            bankCity: [{
                type: 'required',
                message: 'Please select a city'
            }],
            bankBranchName: [{
                type: 'required',
                message: 'Please select a branch'
            }],
            ifscCode: [{
                type: 'required',
                message: 'Please provide an IFSC number'
            }, {
                type: 'pattern',
                message: 'Please enter a valid IFSC code'
            }, {
                type: 'notExist',
                message: 'The IFSC code you have entered does not exist'
            }],
            accountHolderName: [{
                type: 'required',
                message: 'Please enter your name as mentioned in the bank account'
            }],
            accountType: [{
                type: 'required',
                message: 'Please select the account type'
            }],
            accountNumber: [{
                type: 'required',
                message: 'Please provide a valid account number'
            }],
            repeatedAccountNumber: [{
                type: 'required',
                message: 'Please provide a valid account number'
            }],
            accountNumberGroup: [{
                type: 'match',
                message: 'Account Numbers do not match. Please provide the same account number in both the fields.'
            }]
        };
    }

    constructor(protected bankInteractor: BankInteractor, protected bankAccountInteractor: BankAccountInteractor, protected userInteractor: UserInteractor, private loanApplicationInteractor: LoanApplicationInteractor) {
        super();
    }

    ngOnInit(): void {
        this.prepopulate();
    }

    ngAfterViewInit() {
        this.searchBankFormGroup.addControl('bankAccountFormGroup', this.bankAccountDetailsChildComponent.bankAccountFormGroup);
        this.bankAccountDetailsChildComponent.bankAccountFormGroup.setParent(this.searchBankFormGroup);
    }

    async saveBankDetails(): Promise<BankAccount> {
        if (!this.isValid()) {
            Logger.log('Form is invalid. Not proceeding with submission.');
            return;
        }

        const userId = await this.userInteractor.getUserId();
        const accountNumber = this.searchBankFormGroup['controls'].bankAccountFormGroup['controls'].accountNumberGroup['controls'].accountNumber.value;
        const accountType = this.searchBankFormGroup['controls'].bankAccountFormGroup['controls'].accountType.value;
        const accountHolderName = this.searchBankFormGroup['controls'].bankAccountFormGroup['controls'].accountHolderName.value;

        const bankAccount = new BankAccount(
            accountNumber,
            accountType,
            accountHolderName,
            this.searchBankFormGroup.controls.bankName.value,
            this.searchBankFormGroup.controls.bankBranchName.value,
            this.searchBankFormGroup.controls.bankCity.value,
            this.searchBankFormGroup.controls.ifscCode.value,
            userId);

        if (!isEmpty(this.bankAccount) && !isEmpty(this.bankAccount._id)) {
            bankAccount._id = this.bankAccount._id;
            bankAccount.proofs = this.bankAccount.proofs;
            await this.bankAccountInteractor.update(bankAccount);
            this.bankAccount = lodash.clone(bankAccount);
            return bankAccount;
        } else {
            const createdBankAccount = await this.bankAccountInteractor.create(bankAccount);
            this.bankAccount = lodash.clone(createdBankAccount);
            await this.saveBankAccountIdToLoanApplication(createdBankAccount._id);
            return createdBankAccount;
        }
    }

    public isValid(): boolean {
        return super.isValid(this.searchBankFormGroup);
    }

    protected async saveBankAccountIdToLoanApplication(bankAccountId: string): Promise<void> {
        const loanApplication = await this.loanApplicationInteractor.get();
        loanApplication.bankAccountId = bankAccountId;
        await this.loanApplicationInteractor.update(loanApplication);
        this.loanApplicationInteractor.saveApplicationToStorage(loanApplication);
    }

    protected prepopulate(): void {
        if (this.bankAccount) {
            this.searchBankFormGroup.patchValue({
                ifscCode: this.bankAccount.ifsc,
                bankName: this.bankAccount.bank_name,
                bankCity: this.bankAccount.branch_city,
                bankBranchName: this.bankAccount.branch_name
            });

            this.showAccountDetailsSection = true;
        }
    }

    protected clearBankAccountFields(): void {
        this.searchBankFormGroup['controls'].bankAccountFormGroup['controls'].accountType.reset();
        this.searchBankFormGroup['controls'].bankAccountFormGroup['controls'].accountHolderName.reset();
        this.searchBankFormGroup['controls'].bankAccountFormGroup['controls'].accountNumberGroup['controls'].accountNumber.reset();
        this.searchBankFormGroup['controls'].bankAccountFormGroup['controls'].accountNumberGroup['controls'].repeatedAccountNumber.reset();

        this.showAccountDetailsSection = false;
    }

}
